import React, { useEffect, useState } from "react";
import { useTeamsQuery } from "../../../../Services/settingsAPIs/CommonAPI";
import {
  useGetExclusionListsQuery,
  useCreateExclusionListMutation,
  useUpdateExclusionListMutation,
} from "../../../../Services/settingsAPIs/EmailConfiguration";
import { toast } from "react-toastify";
import { ReactMultiEmail } from "react-multi-email";
import ReactModal from "react-modal";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

interface ExclusionData {
  emails: string[];
  contacts: string[];
  domains: string[];
}

// Exclusion type constants
const EXCLUSION_TYPES = {
  FIRST_RESPONSE_SL: "email_first_response_sl_exclusion_list",
  TICKET_CREATION: "email_ticket_creation_exclusion_list",
  FROM_EXCLUSION: "email_from_exclusion_list",
  // DOMAIN_EXCLUSION: "email_domain_exclusion_list",
};

const EXCLUSION_TYPE_LABELS = {
  [EXCLUSION_TYPES.FIRST_RESPONSE_SL]: "First Response SL Exclusion List",
  [EXCLUSION_TYPES.TICKET_CREATION]: "Ticket Creation Exclusion List",
  [EXCLUSION_TYPES.FROM_EXCLUSION]: "From Exclusion List",
  // [EXCLUSION_TYPES.DOMAIN_EXCLUSION]: "Domain Exclusion List",
};

const EXCLUSION_TYPE_DESCRIPTIONS = {
  [EXCLUSION_TYPES.FIRST_RESPONSE_SL]:
    "Exclude emails and domains from First Response SL breach notifications",
  [EXCLUSION_TYPES.TICKET_CREATION]:
    "Prevent ticket creation from specific emails, and domains",
  [EXCLUSION_TYPES.FROM_EXCLUSION]:
    "Block emails from specific sender addresses and domains",
  // [EXCLUSION_TYPES.DOMAIN_EXCLUSION]:
  //   "Exclude entire domains from email processing",
};

const SlExclusionLists = () => {
  const [theTeams, setTheTeams] = useState<any[]>([]);
  const { data: teamsData } = useTeamsQuery({});
  const [selectedTeam, setSelectedTeam] = useState<number>();
  const [selectedExclusionType, setSelectedExclusionType] =
    useState<string>("");
  const [exclusionList, setExclusionList] = useState<any>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const [contacts, setContacts] = useState<string[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [updatedEmails, setUpdatedEmails] = useState<string[]>([]);
  const [updatedContacts, setUpdatedContacts] = useState<string[]>([]);
  const [updatedDomains, setUpdatedDomains] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // Contact input state - using PhoneInput value
  const [phoneInputValue, setPhoneInputValue] = useState("");

  const {
    data: exclusionListsData,
    refetch,
    status,
  } = useGetExclusionListsQuery(
    { teamId: selectedTeam, exclusionType: selectedExclusionType },
    {
      skip: !selectedTeam || !selectedExclusionType,
    },
  );

  const [createExclusionList] = useCreateExclusionListMutation();
  const [updateExclusionList] = useUpdateExclusionListMutation();

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Domain validation function
  // Accepts: @gmail.com, @sub.domain.co.uk, @yahoo.com
  // Also accepts without leading @, and normalizes it on change
  const isValidDomain = (domain: string) => {
    // Strip the leading @ for validation
    const stripped = domain.startsWith("@") ? domain.slice(1) : domain;
    const domainRegex =
      /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(stripped);
  };

  // Normalize domain: ensure it always starts with @
  const normalizeDomain = (domain: string): string => {
    const trimmed = domain.trim();
    if (!trimmed.startsWith("@")) {
      return `@${trimmed}`;
    }
    return trimmed;
  };

  // Contact validation - validates phone number format
  const isValidContact = (contact: string) => {
    // Check if phone number has at least country code and some digits
    // Remove spaces and check if it starts with + and has at least 8 digits total
    const cleanPhone = contact.replace(/\s/g, "");
    return /^\+\d{7,15}$/.test(cleanPhone);
  };

  const handleSelectTeamChange = (event: any) => {
    setSelectedTeam(Number(event.target.value));
    // Reset exclusion type selection when team changes
    setSelectedExclusionType("");
    setExclusionList(null);
    setEmails([]);
    setContacts([]);
    setDomains([]);
    setUpdatedEmails([]);
    setUpdatedContacts([]);
    setUpdatedDomains([]);
  };

  const handleExclusionTypeChange = (type: string) => {
    setSelectedExclusionType(type);
    // Reset state when exclusion type changes
    setExclusionList(null);
    setEmails([]);
    setContacts([]);
    setDomains([]);
    setUpdatedEmails([]);
    setUpdatedContacts([]);
    setUpdatedDomains([]);
    setPhoneInputValue("");
  };

  useEffect(() => {
    if (teamsData) setTheTeams(teamsData);
  }, [teamsData]);

  useEffect(() => {
    if (exclusionListsData) {
      // API returns an array, but we'll work with the first item or create new
      if (Array.isArray(exclusionListsData) && exclusionListsData.length > 0) {
        const list = exclusionListsData[0];
        setExclusionList(list);
        setEmails(list.emails || []);
        setContacts(list.contacts || []);
        // Normalize domains loaded from API to ensure they have @ prefix
        setDomains((list.domains || []).map(normalizeDomain));
        setUpdatedEmails([]);
        setUpdatedContacts([]);
        setUpdatedDomains([]);
      } else {
        setExclusionList(null);
        setEmails([]);
        setContacts([]);
        setDomains([]);
      }
    }
  }, [exclusionListsData]);

  useEffect(() => {
    if (status === "pending") {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [status]);

  const handleEmailChange = (updatedEmailsList: string[]) => {
    setEmails(updatedEmailsList);
    setUpdatedEmails(updatedEmailsList);
  };

  const handleAddContact = () => {
    // Remove all spaces from phone input for validation
    const cleanPhone = phoneInputValue.replace(/\s/g, "");

    if (!cleanPhone || cleanPhone.length < 8) {
      toast.error("Please enter a valid phone number");
      return;
    }

    if (!isValidContact(cleanPhone)) {
      toast.error("Invalid phone number format");
      return;
    }

    if (contacts.includes(cleanPhone)) {
      toast.error("This contact already exists");
      return;
    }

    const updatedContactsList = [...contacts, cleanPhone];
    setContacts(updatedContactsList);
    setUpdatedContacts(updatedContactsList);
    setPhoneInputValue(""); // Reset phone input
  };

  const handleRemoveContact = (contactToRemove: string) => {
    const updatedContactsList = contacts.filter((c) => c !== contactToRemove);
    setContacts(updatedContactsList);
    setUpdatedContacts(updatedContactsList);
  };

  // Normalize domains as they are entered via ReactMultiEmail
  const handleDomainChange = (updatedDomainsList: string[]) => {
    const normalized = updatedDomainsList.map(normalizeDomain);
    setDomains(normalized);
    setUpdatedDomains(normalized);
  };

  // Check if there are any changes
  const hasAnyChanges = () => {
    if (!exclusionList) {
      // New list - check if any field has data
      return emails.length > 0 || contacts.length > 0 || domains.length > 0;
    }

    const originalEmails = exclusionList.emails || [];
    const originalContacts = exclusionList.contacts || [];
    const originalDomains = (exclusionList.domains || []).map(normalizeDomain);

    const emailsChanged =
      JSON.stringify([...emails].sort()) !==
      JSON.stringify([...originalEmails].sort());

    const contactsChanged =
      JSON.stringify([...contacts].sort()) !==
      JSON.stringify([...originalContacts].sort());

    const domainsChanged =
      JSON.stringify([...domains].sort()) !==
      JSON.stringify([...originalDomains].sort());

    return emailsChanged || contactsChanged || domainsChanged;
  };

  const handleSaveAllChanges = async () => {
    if (!selectedTeam) {
      toast.error("Team ID is required");
      return;
    }

    if (!selectedExclusionType) {
      toast.error("Exclusion type is required");
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        team_id: selectedTeam,
        module: "email",
        exclusion_type: selectedExclusionType,
        emails: emails,
        contacts: contacts,
        domains: domains,
      };

      if (exclusionList && exclusionList.id) {
        // Update existing list - send all current data
        const updatePayload: any = {
          emails: emails,
          contacts: contacts,
          domains: domains,
        };

        await updateExclusionList({
          listId: exclusionList.id,
          body: updatePayload,
          team_id: selectedTeam,
          exclusion_type: selectedExclusionType,
        }).unwrap();

        toast.success("Exclusion list updated successfully");
      } else {
        // Create new list
        await createExclusionList({ body: payload }).unwrap();
        toast.success("Exclusion list created successfully");
      }

      setUpdatedEmails([]);
      setUpdatedContacts([]);
      setUpdatedDomains([]);
      refetch();
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error saving exclusion list:", error);
      toast.error(
        error?.data?.message ||
          "Failed to save exclusion list. Please try again.",
      );
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          /* Responsive utilities */
          @media (max-width: 767px) {
            .mobile-full-width {
              width: 100% !important;
            }
            
            .mobile-text-center {
              text-align: center !important;
            }
            
            .mobile-stack {
              flex-direction: column !important;
            }
            
            .mobile-mb-3 {
              margin-bottom: 1rem !important;
            }
            
            .card-toolbar {
              margin-top: 1rem;
            }
            
            /* Adjust font sizes for mobile */
            .fs-2x {
              font-size: 2rem !important;
            }
            
            /* Phone input responsiveness */
            .react-international-phone-input-container {
              width: 100% !important;
            }
            
            .react-international-phone-input {
              width: 100% !important;
            }
            
            /* Multi-email responsiveness */
            .badge {
              font-size: 0.8rem !important;
              padding: 0.4rem 0.6rem !important;
            }
            
            /* Button adjustments */
            .btn-sm {
              font-size: 0.875rem;
              padding: 0.375rem 0.75rem;
            }
          }
          
          @media (max-width: 575px) {
            /* Extra small screens */
            .card-body {
              padding: 1rem !important;
            }
            
            .p-6 {
              padding: 1rem !important;
            }
            
            .px-4 {
              padding-left: 0.75rem !important;
              padding-right: 0.75rem !important;
            }
            
            .mb-8 {
              margin-bottom: 1.5rem !important;
            }
            
            .mb-6 {
              margin-bottom: 1rem !important;
            }
            
            /* Adjust icon sizes */
            .symbol-50px {
              width: 40px !important;
              height: 40px !important;
            }
            
            .symbol-label {
              width: 40px !important;
              height: 40px !important;
            }
            
            .symbol-label i {
              font-size: 1.5rem !important;
            }
          }
          
          /* Tablet responsiveness */
          @media (min-width: 768px) and (max-width: 991px) {
            .col-md-6 {
              width: 100% !important;
              margin-bottom: 1rem;
            }
          }
          
          /* Contact badges responsiveness */
          .contact-badge-wrapper {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          
          @media (max-width: 767px) {
            .contact-badge-wrapper .badge {
              font-size: 0.75rem;
              padding: 0.5rem 0.75rem;
              word-break: break-all;
            }
          }
          
          /* Modal responsiveness */
          @media (max-width: 767px) {
            .modal-content {
              width: 95vw !important;
              max-width: 95vw !important;
            }
            
            .modal-body {
              padding: 1.5rem !important;
            }
          }
          
          /* Ensure proper wrapping of email tags */
          [data-tag] {
            word-break: break-word;
            max-width: 100%;
          }
          
          /* Flexbox improvements */
          .d-flex.gap-3 {
            gap: 0.75rem;
          }
          
          @media (max-width: 575px) {
            .d-flex.gap-3 {
              gap: 0.5rem;
            }
          }
          
          /* Phone input container */
          .phone-input-wrapper {
            width: 100%;
          }
          
          @media (max-width: 767px) {
            .phone-input-wrapper .d-flex {
              flex-direction: column;
              align-items: stretch !important;
            }
            
            .phone-input-wrapper .btn {
              margin-top: 0.5rem !important;
              width: 100%;
            }
          }
        `}
      </style>

      <div className="card">
        <div className="card-header border-0 pt-6">
          <div className="card-title">
            <h3 className="fw-bold m-0">SL Exclusion Lists</h3>
          </div>
          <div className="card-toolbar">
            <button
              className="btn btn-sm btn-light-primary"
              onClick={() => setShowInfoPopup(true)}
            >
              <i className="bi bi-info-circle me-2"></i>
              <span className="d-none d-sm-inline">Help</span>
              <span className="d-inline d-sm-none">Info</span>
            </button>
          </div>
        </div>

        <div className="card-body py-4">
          {/* Team Selection Section */}
          <div className="row mb-8">
            <div className="col-12">
              <div className="bg-light-primary rounded p-6">
                <div className="d-flex align-items-center mb-4 flex-wrap">
                  <i className="bi bi-people-fill fs-2x text-primary me-3"></i>
                  <div className="flex-grow-1">
                    <h4 className="mb-0">Select Team</h4>
                    <p className="text-muted mb-0 fs-7">
                      Choose the team to configure exclusion lists
                    </p>
                  </div>
                </div>
                <select
                  className="form-select form-select-lg"
                  onChange={handleSelectTeamChange}
                  value={selectedTeam || ""}
                >
                  <option value="" disabled>
                    Select a team to continue
                  </option>
                  {theTeams?.map((el: any) => (
                    <option value={el?.id} key={el?.id}>
                      {el?.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Exclusion Type Selection - Only show after team is selected */}
          {selectedTeam && !selectedExclusionType && (
            <div className="row">
              <div className="col-12">
                <div className="mb-6">
                  <h4 className="mb-6">Select Exclusion Type</h4>
                  <div className="row g-4">
                    {Object.entries(EXCLUSION_TYPES).map(([key, value]) => (
                      <div className="col-12 col-md-6" key={value}>
                        <div
                          className="card card-custom card-stretch shadow-sm cursor-pointer h-100 hover-elevate-up"
                          onClick={() => handleExclusionTypeChange(value)}
                          style={{ transition: "all 0.3s ease" }}
                        >
                          <div className="card-body d-flex flex-column">
                            <div className="d-flex align-items-start mb-4">
                              <div className="symbol symbol-50px me-3 me-sm-4 flex-shrink-0">
                                <span className="symbol-label bg-light-primary">
                                  <i className="bi bi-shield-fill-exclamation fs-2x text-primary"></i>
                                </span>
                              </div>
                              <div className="flex-grow-1">
                                <h5 className="mb-2">
                                  {EXCLUSION_TYPE_LABELS[value]}
                                </h5>
                                <p className="text-muted mb-0 fs-7">
                                  {EXCLUSION_TYPE_DESCRIPTIONS[value]}
                                </p>
                              </div>
                            </div>
                            <div className="mt-auto">
                              <button className="btn btn-sm btn-light-primary w-100">
                                <i className="bi bi-arrow-right me-2"></i>
                                Configure
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Section - Only show after exclusion type is selected */}
          {selectedTeam && selectedExclusionType && (
            <>
              {/* Breadcrumb / Back Button */}
              <div className="mb-6">
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => {
                    setSelectedExclusionType("");
                    setPhoneInputValue("");
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  <span className="d-none d-sm-inline">
                    Back to Exclusion Types
                  </span>
                  <span className="d-inline d-sm-none">Back</span>
                </button>
              </div>

              {/* Current Configuration Header */}
              <div className="alert alert-primary d-flex align-items-center mb-8 flex-wrap">
                <i className="bi bi-info-circle text-dark fs-2x me-3 me-sm-4 flex-shrink-0"></i>
                <div className="flex-grow-1">
                  <h5 className="mb-1">
                    {EXCLUSION_TYPE_LABELS[selectedExclusionType]}
                  </h5>
                  <p className="mb-0 fs-7">
                    {EXCLUSION_TYPE_DESCRIPTIONS[selectedExclusionType]}
                  </p>
                </div>
              </div>

              <div style={{ maxHeight: "30vh", overflowY: "auto", overflowX: "hidden" }}>
              {/* Domains Card */}
              <div className="col-12 mb-4">
                <div className="card shadow-sm">
                  <div className="card-header">
                    <h4 className="card-title">
                      <i className="bi bi-globe text-warning me-2"></i>
                      Domains
                    </h4>
                  </div>
                  <div className="card-body">
                    <p className="text-muted mb-4 fs-7">
                      Add domains to exclude using the <code>@domain.com</code>{" "}
                      format. You can type with or without the <code>@</code> —
                      it will be added automatically. Press Enter or comma after
                      each domain.
                    </p>

                    {/* Hint badge row */}
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      {["@gmail.com", "@yahoo.com", "@hotmail.com"].map(
                        (example) => (
                          <span
                            key={example}
                            className="badge badge-light-secondary fs-7"
                          >
                            {example}
                          </span>
                        ),
                      )}
                      <span className="text-muted fs-7 align-self-center">
                        — example format
                      </span>
                    </div>

                    <ReactMultiEmail
                      placeholder="e.g., @gmail.com, @yahoo.com, @company.org"
                      emails={domains}
                      onChange={handleDomainChange}
                      validateEmail={(domain) => isValidDomain(domain)}
                      getLabel={(domain, domainIndex, removeDomain) => (
                        <div
                          data-tag
                          key={domainIndex}
                          className="badge badge-light-warning me-2 mb-2 fs-6"
                        >
                          {/* Always display with @ prefix */}
                          <span className="me-2">
                            {domain.startsWith("@") ? domain : `@${domain}`}
                          </span>
                          <span
                            data-tag-handle
                            onClick={() => removeDomain(domainIndex)}
                            className="cursor-pointer"
                          >
                            <i className="bi bi-x"></i>
                          </span>
                        </div>
                      )}
                      style={{ minHeight: "80px" }}
                    />
                    {domains.length > 0 && (
                      <div className="mt-3">
                        <span className="badge badge-light-info">
                          {domains.length} domain
                          {domains.length !== 1 ? "s" : ""} added
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contacts Card */}
              {/* <div className="col-12 mb-4">
                <div className="card shadow-sm">
                  <div className="card-header">
                    <h4 className="card-title">
                      <i className="bi bi-telephone-fill text-success me-2"></i>
                      Contact Numbers
                    </h4>
                  </div>
                  <div className="card-body">
                    <p className="text-muted mb-4 fs-7">
                      Add contact numbers with country codes to exclude from
                      this configuration.
                    </p>

                    <div className="phone-input-wrapper mb-4">
                      <div className="d-flex gap-3 align-items-start">
                        <div className="flex-grow-1">
                          <PhoneInput
                            defaultCountry="IN"
                            value={phoneInputValue || "+91"}
                            onChange={(phone) => setPhoneInputValue(phone)}
                            placeholder="Enter phone number"
                            inputClassName="form-control"
                            countrySelectorStyleProps={{
                              buttonClassName: "btn",
                            }}
                            style={{ width: "100%" }}
                          />
                          <small className="text-muted d-block mt-2">
                            Select country and enter phone number, then click
                            Add
                          </small>
                        </div>
                        <button
                          className="btn btn-primary px-5 py-3"
                          onClick={handleAddContact}
                          disabled={
                            !phoneInputValue || phoneInputValue.length < 8
                          }
                          style={{ marginTop: "0" }}
                        >
                          <i className="bi bi-plus-lg me-2"></i>
                          <span className="d-none d-sm-inline">Add</span>
                          <span className="d-inline d-sm-none">+</span>
                        </button>
                      </div>
                    </div>

                    {contacts.length > 0 && (
                      <div>
                        <div className="separator separator-dashed mb-4"></div>
                        <div className="contact-badge-wrapper">
                          {contacts.map((contact, index) => (
                            <div
                              key={index}
                              className="badge badge-light-success fs-6 d-flex align-items-center py-3 px-4"
                            >
                              <i className="bi bi-phone me-2 flex-shrink-0"></i>
                              <span className="me-3">{contact}</span>
                              <span
                                className="cursor-pointer flex-shrink-0"
                                onClick={() => handleRemoveContact(contact)}
                                title="Remove contact"
                              >
                                <i className="bi bi-x-lg"></i>
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3">
                          <span className="badge badge-light-info">
                            {contacts.length} contact
                            {contacts.length !== 1 ? "s" : ""} added
                          </span>
                        </div>
                      </div>
                    )}

                    {contacts.length === 0 && (
                      <div className="text-center py-6 text-muted">
                        <i className="bi bi-telephone-x fs-3x mb-3 d-block"></i>
                        <p className="mb-0">No contacts added yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>  */}
              {/* Configuration Cards */}
              <div className="row g-4 g-sm-6 mb-4">
                {/* Emails Card */}
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header">
                      <h4 className="card-title">
                        <i className="bi bi-envelope-fill text-primary me-2"></i>
                        Email Addresses
                      </h4>
                    </div>
                    <div className="card-body">
                      <p className="text-muted mb-4 fs-7">
                        Add email addresses to exclude from this configuration.
                        Press Enter or comma after each email.
                      </p>
                      <ReactMultiEmail
                        placeholder="e.g., spam@example.com, test@domain.com"
                        emails={emails}
                        onChange={handleEmailChange}
                        validateEmail={(email) => isValidEmail(email)}
                        getLabel={(email, emailIndex, removeEmail) => (
                          <div
                            data-tag
                            key={emailIndex}
                            className="badge badge-light-primary me-2 mb-2 fs-6"
                          >
                            <span className="me-2">{email}</span>
                            <span
                              data-tag-handle
                              onClick={() => removeEmail(emailIndex)}
                              className="cursor-pointer"
                            >
                              <i className="bi bi-x"></i>
                            </span>
                          </div>
                        )}
                        style={{ minHeight: "80px" }}
                      />
                      {emails.length > 0 && (
                        <div className="mt-3">
                          <span className="badge badge-light-info">
                            {emails.length} email
                            {emails.length !== 1 ? "s" : ""} added
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </div>

              {/* Save Button */}
              <div className="d-flex justify-content-end gap-3 flex-wrap">
                <button
                  className="btn btn-light"
                  onClick={() => {
                    setEmails(exclusionList?.emails || []);
                    setContacts(exclusionList?.contacts || []);
                    setDomains(
                      (exclusionList?.domains || []).map(normalizeDomain),
                    );
                    setUpdatedEmails([]);
                    setUpdatedContacts([]);
                    setUpdatedDomains([]);
                    setPhoneInputValue("");
                  }}
                  disabled={!hasAnyChanges() || isLoading}
                >
                  Reset
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveAllChanges}
                  disabled={!hasAnyChanges() || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      <span className="d-none d-sm-inline">
                        Save Configuration
                      </span>
                      <span className="d-inline d-sm-none">Save</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Help Modal Styles */}
      <style>{`
        .help-modal-header {
          padding: 1.75rem 2rem 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
        }

        .help-modal-header-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .help-modal-close {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: #f1f5f9;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.15s, color 0.15s;
        }

        .help-modal-close:hover {
          background: #e2e8f0;
          color: #334155;
        }

        .help-modal-body {
          overflow-y: auto;
          padding: 1.75rem 2rem;
          flex: 1;
        }

        .help-section-label {
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #94a3b8;
          margin-bottom: 0.875rem;
        }

        .help-type-card {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          padding: 1rem 1.125rem;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: #fafbfc;
          margin-bottom: 0.625rem;
          transition: border-color 0.15s, background 0.15s;
        }

        .help-type-card:last-child {
          margin-bottom: 0;
        }

        .help-type-card:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .help-type-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
          margin-top: 5px;
        }

        .help-type-card-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
          line-height: 1.4;
        }

        .help-type-card-desc {
          font-size: 0.8125rem;
          color: #64748b;
          line-height: 1.5;
          margin: 0;
        }

        .help-notice {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          border-radius: 10px;
          margin-bottom: 0.625rem;
        }

        .help-notice-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .help-notice.warning {
          background: #fffbeb;
          border: 1px solid #fde68a;
        }

        .help-notice.warning .help-notice-icon {
          background: #fef3c7;
          color: #d97706;
        }

        .help-notice.info {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
        }

        .help-notice.info .help-notice-icon {
          background: #e0f2fe;
          color: #4f46e5;
        }

        .help-notice-title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.2rem;
        }

        .help-notice-text {
          font-size: 0.8rem;
          color: #475569;
          line-height: 1.5;
          margin: 0;
        }

        .help-divider {
          height: 1px;
          background: #f1f5f9;
          margin: 1.25rem 0;
        }

        .help-modal-footer {
          padding: 1.125rem 2rem;
          border-top: 1px solid #f1f5f9;
          display: flex;
          justify-content: flex-end;
          background: #fafbfc;
        }

        .help-modal-footer .btn-primary {
          padding: 0.5rem 1.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          border-radius: 8px;
        }

        @media (max-width: 575px) {
          .help-modal-body {
            padding: 1.25rem;
          }
          .help-modal-header {
            padding: 1.25rem;
          }
          .help-modal-footer {
            padding: 1rem 1.25rem;
          }
        }
      `}</style>

      {/* Info Popup Modal */}
      <ReactModal
        isOpen={showInfoPopup}
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            padding: "0",
            border: "none",
            borderRadius: "16px",
            width: "620px",
            maxWidth: "95vw",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow:
              "0 25px 60px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.1)",
          },
          overlay: {
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            zIndex: 1050,
          },
        }}
        onRequestClose={() => setShowInfoPopup(false)}
        contentLabel="Exclusion Lists Help"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            background: "#fff",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div className="help-modal-header">
            <div className="d-flex align-items-center gap-3">
              <div className="help-modal-header-icon">
                <i className="bi bi-shield-check text-white fs-5"></i>
              </div>
              <div>
                <h5
                  className="mb-0 fw-bold"
                  style={{ color: "#0f172a", fontSize: "1rem" }}
                >
                  Exclusion Lists Guide
                </h5>
                <p className="mb-0 fs-7 text-muted mt-1">
                  How email exclusions work and what each type does
                </p>
              </div>
            </div>
            <button
              className="help-modal-close"
              onClick={() => setShowInfoPopup(false)}
            >
              <i className="bi bi-x-lg" style={{ fontSize: "0.8rem" }}></i>
            </button>
          </div>

          {/* Body */}
          <div className="help-modal-body">
            {/* Exclusion Types Section */}
            <div className="help-section-label">Exclusion Types</div>

            <div className="help-type-card">
              <div
                className="help-type-dot"
                style={{ background: "#6366f1" }}
              ></div>
              <div>
                <div className="help-type-card-title">First Response SL</div>
                <p className="help-type-card-desc">
                  Emails and domains added here are excluded from First Response
                  SL breach notifications. They will{" "}
                  <strong>not have SL applied</strong> and will be marked as{" "}
                  <strong>non-actionable</strong>.
                </p>
              </div>
            </div>

            <div className="help-type-card">
              <div
                className="help-type-dot"
                style={{ background: "#10b981" }}
              ></div>
              <div>
                <div className="help-type-card-title">Ticket Creation</div>
                <p className="help-type-card-desc">
                  Prevents automatic ticket creation from specified emails and
                  domains. Inbound emails from these sources will be silently
                  ignored.
                </p>
              </div>
            </div>

            <div className="help-type-card">
              <div
                className="help-type-dot"
                style={{ background: "#f59e0b" }}
              ></div>
              <div>
                <div className="help-type-card-title">From Exclusion</div>
                <p className="help-type-card-desc">
                  Blocks processing of emails from specific sender addresses or
                  entire domains. Use this to filter out known spam or internal
                  no-reply senders.
                </p>
              </div>
            </div>

            <div className="help-divider"></div>

            {/* Notices */}
            <div className="help-section-label">Important Notes</div>

            <div className="help-notice warning">
              <div className="help-notice-icon">
                <i
                  className="bi bi-exclamation-triangle-fill"
                  style={{ fontSize: "0.875rem" }}
                ></i>
              </div>
              <div>
                <div className="help-notice-title">
                  SL not applied for excluded emails
                </div>
                <p className="help-notice-text">
                  Emails from the SL exclusion list will not have SL applied and
                  will be marked as <strong>non-actionable</strong>{" "}
                  automatically.
                </p>
              </div>
            </div>

            <div className="help-notice info">
              <div className="help-notice-icon">
                <i
                  className="bi bi-at"
                  style={{ fontSize: "1rem", fontWeight: 700 }}
                ></i>
              </div>
              <div>
                <div className="help-notice-title">Domain format</div>
                <p className="help-notice-text">
                  Domains should be entered as <code>@gmail.com</code>,{" "}
                  <code>@yahoo.com</code>, etc. The <code>@</code> prefix is
                  added automatically if you omit it.
                </p>
              </div>
            </div>

            <div className="help-notice info" style={{ marginBottom: 0 }}>
              <div className="help-notice-icon">
                <i
                  className="bi bi-layers-fill"
                  style={{ fontSize: "0.875rem" }}
                ></i>
              </div>
              <div>
                <div className="help-notice-title">
                  Separate configurations per type
                </div>
                <p className="help-notice-text">
                  Each exclusion type maintains its own independent list of
                  emails and domains. Changes to one type do not affect others.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="help-modal-footer">
            <button
              className="btn btn-primary"
              onClick={() => setShowInfoPopup(false)}
            >
              Got it
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default SlExclusionLists;
